import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, shortDate, relTime } from '../money.js';
import { Empty, Spinner, Avatar, EntityGlyph, Kpi, Overlay, Chip } from '../ui.jsx';

/**
 * Accounts — the AE half.
 *
 * The columns are chosen for one question: which of my clients needs me this
 * week? So health, live work and days-since-touch sit next to the next action,
 * and the whole table can be filtered to just the ones that are due.
 */
export default function Accounts({ me, onOpenClient, toast }) {
  const { t, lang } = useLang();
  const [mine, setMine] = useState(me.role === 'ae');
  const [due, setDue] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [servicing, setServicing] = useState(null);

  const load = useCallback(() => {
    setData(null);
    api
      .accounts({ mine: mine ? '1' : '', due: due ? '1' : '' })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [mine, due]);

  useEffect(() => {
    load();
  }, [load]);

  const healthChip = (h) => {
    if (!h) return <span style={{ color: 'var(--ink-3)' }}>—</span>;
    const map = {
      good: ['var(--success)', 'var(--success-soft)', t('good')],
      watch: ['var(--warning)', 'var(--warning-soft)', t('watch')],
      at_risk: ['var(--danger)', 'var(--danger-soft)', t('atRisk')],
    };
    const [fg, bg, label] = map[h] || map.good;
    return (
      <span className="pill" style={{ background: bg, color: fg }}>
        {label}
      </span>
    );
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '18px 24px 6px',
        }}
      >
        <div style={{ marginRight: 'auto' }}>
          <div className="display" style={{ fontSize: 22 }}>
            {t('navAccounts')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
            {t('accountsBlurb')}
          </div>
        </div>
        <button className={`chip${mine ? ' is-on' : ''}`} onClick={() => setMine((v) => !v)}>
          {mine ? me.name.split(' ')[0] : t('allOwners')}
        </button>
        <button className={`chip${due ? ' is-on' : ''}`} onClick={() => setDue((v) => !v)}>
          {t('dueOnly')}
        </button>
      </div>

      {error && <div className="page">{error}</div>}
      {!error && !data && <Spinner label={t('loading')} />}

      {data && (
        <div style={{ padding: '4px 24px 24px' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi label={t('navAccounts')} value={data.kpis.accounts ?? 0} />
            <Kpi label={t('atRisk')} value={data.kpis.at_risk ?? 0} />
            <Kpi
              label={t('untouchedFor', { n: data.touch_warn_days })}
              value={data.kpis.untouched ?? 0}
            />
            <Kpi label="Unassigned" value={data.kpis.unassigned ?? 0} />
          </div>

          {data.accounts.length === 0 ? (
            <Empty>{t('noAccountsYet')}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('account')}</th>
                    <th>{t('contact')}</th>
                    <th>AE</th>
                    <th>{t('health')}</th>
                    <th style={{ textAlign: 'right' }}>{t('activeWork')}</th>
                    <th style={{ textAlign: 'right' }}>{t('revenueYtd')}</th>
                    <th>{t('nextActionCol')}</th>
                    <th>{t('lastTouch')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.accounts.map((a) => (
                    <tr key={a.id}>
                      <td
                        style={{ whiteSpace: 'normal', minWidth: 200, cursor: 'pointer' }}
                        onClick={() => onOpenClient(a.id)}
                      >
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 600 }}
                        >
                          <EntityGlyph entity={a.entity_tag} />
                          {a.name}
                        </span>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {a.engagement_type ? `${a.engagement_type} · ` : ''}
                          {t('since')} {shortDate(a.since, lang)}
                        </div>
                      </td>
                      <td>
                        {a.contact_name || <span style={{ color: 'var(--ink-3)' }}>—</span>}
                        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                          {a.contact_phone || a.contact_email || ''}
                        </div>
                      </td>
                      <td>
                        {a.ae_owner_name ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Avatar initials={a.ae_owner_initials} seed={a.ae_owner_var} size={20} />
                            {a.ae_owner_name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--warning)', fontWeight: 600, fontSize: 12 }}>
                            Unassigned
                          </span>
                        )}
                      </td>
                      <td>{healthChip(a.health)}</td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {a.active_projects}
                        {a.open_upsells > 0 && (
                          <span style={{ color: 'var(--entity-mr)', fontWeight: 700 }}>
                            {' '}
                            +{a.open_upsells}
                          </span>
                        )}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {thb(a.revenue_ytd_satang)}
                      </td>
                      <td style={{ whiteSpace: 'normal', maxWidth: 220 }}>
                        {a.next_action ? (
                          <>
                            <div style={{ fontSize: 12.5 }}>{a.next_action}</div>
                            <div
                              style={{
                                fontSize: 11.5,
                                fontWeight: 700,
                                color:
                                  a.next_action_at &&
                                  a.next_action_at <= new Date().toISOString().slice(0, 10)
                                    ? 'var(--warning)'
                                    : 'var(--ink-3)',
                              }}
                            >
                              {t('by')} {shortDate(a.next_action_at, lang)}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: 'var(--ink-3)' }}>—</span>
                        )}
                      </td>
                      <td
                        style={{
                          color:
                            a.days_since_touch > data.touch_warn_days
                              ? 'var(--warning)'
                              : 'var(--ink-3)',
                          fontWeight: a.days_since_touch > data.touch_warn_days ? 700 : 400,
                        }}
                      >
                        {a.days_since_touch}d
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-sm btn-primary" onClick={() => setServicing(a)}>
                          {t('logServiceTitle')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {servicing && (
        <ServiceModal
          account={servicing}
          onClose={() => setServicing(null)}
          onDone={() => {
            setServicing(null);
            toast(t('serviceLogged'));
            load();
          }}
        />
      )}
    </>
  );
}

/**
 * Log service. Deliberately asks for the next action in the same breath: an
 * account should never leave this form without one, which is the difference
 * between a servicing rhythm and a list of things that happened.
 */
function ServiceModal({ account, onClose, onDone }) {
  const { t } = useLang();
  const [f, setF] = useState({
    subject: '',
    body: '',
    health: account.health || 'good',
    next_action: '',
    next_action_at: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.logService(account.id, {
        subject: f.subject,
        body: f.body || undefined,
        health: f.health,
        next_action: f.next_action || null,
        next_action_at: f.next_action_at || null,
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose} kind="modal">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '18px 20px 10px' }}>
          <div className="display" style={{ fontSize: 18 }}>
            {t('logServiceTitle')}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>{account.name}</div>
        </div>

        <div style={{ padding: '0 20px', overflow: 'auto' }}>
          <label className="field-label">{t('whatHappened')}</label>
          <input
            className="input"
            required
            autoFocus
            value={f.subject}
            onChange={(e) => set('subject', e.target.value)}
          />

          <label className="field-label" style={{ marginTop: 12 }}>
            {t('details')}
          </label>
          <textarea
            className="textarea"
            rows={3}
            value={f.body}
            onChange={(e) => set('body', e.target.value)}
          />

          <label className="field-label" style={{ marginTop: 14 }}>
            {t('setHealth')}
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              ['good', t('good')],
              ['watch', t('watch')],
              ['at_risk', t('atRisk')],
            ].map(([v, label]) => (
              <Chip key={v} on={f.health === v} onClick={() => set('health', v)}>
                {label}
              </Chip>
            ))}
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: '1px solid var(--line)',
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: 10,
            }}
          >
            <div>
              <label className="field-label">{t('setNextAction')}</label>
              <input
                className="input"
                value={f.next_action}
                onChange={(e) => set('next_action', e.target.value)}
                placeholder="Send route options"
              />
            </div>
            <div>
              <label className="field-label">{t('by')}</label>
              <input
                className="input"
                type="date"
                value={f.next_action_at}
                onChange={(e) => set('next_action_at', e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 12 }}>{error}</div>
          )}
        </div>

        <div className="drawer-foot" style={{ marginTop: 14 }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            {t('cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !f.subject}>
            {t('logIt')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}
